import type {
  BuildingView,
  QualityTier,
  SettlementDistrictView,
  SettlementViewModel,
  WorldCapabilityView,
} from '@aztec-realms/shared-types';

import {
  resolveCapabilityOverlay,
  resolveDistrictOverlay,
  type CapabilityOverlayAsset,
  type DistrictOverlayAsset,
} from './asset-manifest';
import { resolveConstructionStage } from './construction-stage';

/** Only changes visible to Phaser should rebuild its persistent ambient layers. */
export function settlementSceneRevision(snapshot: SettlementViewModel): string {
  return JSON.stringify({
    visualTier: snapshot.visualTier,
    ambientPopulation: snapshot.ambientPopulation,
    districts: snapshot.districts,
    worldCapabilities: snapshot.worldCapabilities,
    buildings: snapshot.buildings.map(({ progress, ...building }) => ({
      ...building,
      constructionStage:
        building.state === 'constructing'
          ? resolveConstructionStage(progress ?? 0).stage
          : undefined,
    })),
  });
}

export interface SettlementDistrictSceneEntry {
  district: SettlementDistrictView;
  overlay: DistrictOverlayAsset;
}

export interface SettlementCapabilitySceneEntry {
  capability: WorldCapabilityView;
  overlay: CapabilityOverlayAsset;
}

export interface SettlementScenePlan {
  progression: 0 | 1 | 2 | 3;
  buildings: readonly BuildingView[];
  districts: readonly SettlementDistrictSceneEntry[];
  capabilitiesByBuilding: Readonly<Record<string, readonly SettlementCapabilitySceneEntry[]>>;
  terraceCount: number;
  bridgeCount: number;
  marketStallCount: number;
  chinampaPlotCount: number;
  civicStructureCount: number;
  vegetationCount: number;
  bannerCount: number;
  npcCount: number;
  animatedNpcCount: number;
  boatCount: number;
  rippleCount: number;
  birdCount: number;
}

const qualityLimits: Record<
  Exclude<QualityTier, 'auto'>,
  {
    animatedNpcs: number;
    birds: number;
    boats: number;
    npcs: number;
    ripples: number;
  }
> = {
  low: { animatedNpcs: 0, birds: 0, boats: 1, npcs: 15, ripples: 0 },
  medium: { animatedNpcs: 12, birds: 2, boats: 3, npcs: 40, ripples: 6 },
  high: { animatedNpcs: 24, birds: 5, boats: 5, npcs: 64, ripples: 11 },
};

function resolvedQuality(qualityTier: QualityTier): Exclude<QualityTier, 'auto'> {
  return qualityTier === 'auto' ? 'medium' : qualityTier;
}

export function createSettlementScenePlan(
  snapshot: SettlementViewModel,
  qualityTier: QualityTier,
): SettlementScenePlan {
  const progression = Math.min(3, Math.max(0, snapshot.visualTier)) as 0 | 1 | 2 | 3;
  const limits = qualityLimits[resolvedQuality(qualityTier)];
  const npcCount = Math.min(snapshot.ambientPopulation, limits.npcs);
  const capabilitiesByBuilding: Record<string, SettlementCapabilitySceneEntry[]> = {};
  for (const capability of snapshot.worldCapabilities) {
    const overlay = resolveCapabilityOverlay(capability.code);
    if (!overlay) continue;
    (capabilitiesByBuilding[capability.sourceBuildingId] ??= []).push({ capability, overlay });
  }

  return {
    progression,
    buildings: [...snapshot.buildings].sort(
      (left, right) => left.gridX + left.gridY - (right.gridX + right.gridY),
    ),
    districts: snapshot.districts
      .map((district) => {
        const overlay = resolveDistrictOverlay(district.code);
        return overlay ? { district, overlay } : undefined;
      })
      .filter((entry): entry is SettlementDistrictSceneEntry => entry !== undefined)
      .sort((left, right) => left.district.unlockOrder - right.district.unlockOrder),
    capabilitiesByBuilding,
    terraceCount: [2, 4, 7, 11][progression],
    bridgeCount: [2, 4, 6, 9][progression],
    marketStallCount: [1, 4, 8, 13][progression],
    chinampaPlotCount: [2, 4, 7, 10][progression],
    civicStructureCount: [0, 2, 5, 9][progression],
    vegetationCount: [15, 28, 48, 72][progression],
    bannerCount: [1, 4, 8, 12][progression],
    npcCount,
    animatedNpcCount: Math.min(npcCount, limits.animatedNpcs),
    boatCount: Math.min(limits.boats, [1, 2, 4, 6][progression]),
    rippleCount: limits.ripples,
    birdCount: limits.birds,
  };
}
